import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authService } from '../services'

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      loading: false,
      error: null,
      _hasHydrated: false,

      setHasHydrated: (state) => {
        set({ _hasHydrated: state })
      },

      login: async (email, password) => {
        set({ loading: true, error: null })
        try {
          const data = await authService.login(email, password)
          const { user, access_token } = data
          
          // Store token in localStorage for API interceptor
          if (access_token) {
            localStorage.setItem('auth-token', access_token)
          }
          
          set({ 
            user, 
            token: access_token, 
            isAuthenticated: true, 
            loading: false 
          })
          
          return { success: true }
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Login failed'
          set({ error: errorMessage, loading: false })
          return { success: false, error: errorMessage }
        }
      },

      register: async (name, email, password, password_confirmation) => {
        set({ loading: true, error: null })
        try {
          const data = await authService.register(name, email, password, password_confirmation)
          const { user, access_token } = data
          
          // Store token in localStorage for API interceptor
          if (access_token) {
            localStorage.setItem('auth-token', access_token)
          }
          
          set({ 
            user, 
            token: access_token, 
            isAuthenticated: true, 
            loading: false 
          })
          
          return { success: true }
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Registration failed'
          set({ error: errorMessage, loading: false })
          return { success: false, error: errorMessage }
        }
      },

      logout: async () => {
        // Clear token and state immediately
        localStorage.removeItem('auth-token')
        set({ user: null, token: null, isAuthenticated: false })
        
        // Try to call logout API (best effort)
        try {
          await authService.logout()
        } catch (error) {
          console.error('Logout API error (ignoring):', error)
          // Continue anyway - user is already logged out locally
        }
        
        // Redirect to login page
        window.location.href = '/login'
      },

      updateUser: (userData) => {
        set({ user: { ...get().user, ...userData } })
      },

      // Refresh user data
      refreshUser: async () => {
        try {
          const data = await authService.getUser()
          set({ user: data.user })
          return { success: true }
        } catch (error) {
          console.error('Failed to refresh user:', error)
          return { success: false }
        }
      },

      // Initialize auth from storage
      initialize: () => {
        const { token } = get()
        if (token) {
          localStorage.setItem('auth-token', token)
        }
      },

      // OAuth login - sets token and fetches user
      loginWithOAuth: async (token) => {
        set({ loading: true, error: null })
        try {
          // Store token in localStorage for API interceptor
          localStorage.setItem('auth-token', token)
          set({ token, isAuthenticated: true })
          
          // Fetch user data
          const data = await authService.getUser()
          
          set({ 
            user: data.user, 
            loading: false 
          })
          
          return { success: true }
        } catch (error) {
          console.error('OAuth login failed - Full error:', error);
          console.error('OAuth login failed - Response:', error.response);
          console.error('OAuth login failed - Status:', error.response?.status);
          console.error('OAuth login failed - Data:', error.response?.data);
          const errorMessage = error.response?.data?.message || 'OAuth login failed'
          set({ error: errorMessage, loading: false, token: null, isAuthenticated: false })
          localStorage.removeItem('auth-token')
          return { success: false, error: errorMessage }
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        token: state.token, 
        isAuthenticated: state.isAuthenticated 
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    }
  )
)

// Initialize on load
useAuthStore.getState().initialize()
